import { ModalRouteService } from './modal-route.service';
import { throwError as observableThrowError, Observable, of, combineLatest, ReplaySubject, Subscription, interval, Subject } from 'rxjs';

import { catchError, map, mergeMap, share, first, tap } from 'rxjs/operators';
import { LocalStorageService } from 'angular-2-local-storage';
import { SapiService, SapiEntity } from './sapi.service';
import { Injectable } from '@angular/core';
import { User } from '../interfaces/user';
import { Publication, CustomField } from '../interfaces/publication';
import { PublicationService } from './publication.service';

import { Md5 } from 'ts-md5';
import { Router, RouterStateSnapshot } from '@angular/router';
import { BbSelectOption } from '../components/shared/bb-select/bb-select.component';
import { LocationService } from './location.service';
import { OverlayService, NoticeDefinition } from './overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Bowser from 'bowser';

const md5 = new Md5();

@Injectable()
export class AuthenticationService {
	public server: string;
	public publicationsSubject: ReplaySubject<BbSelectOption[]>;
	public angularWantsToNavigateSubject: ReplaySubject<boolean> = new ReplaySubject(1);

	private userData: User;
	private userDataObs: ReplaySubject<User>;
	private userDataRequestObs: Observable<any>;
	private switchUserRequestObs: Observable<any>;
	private selectedPublication: string;
	private isGlobalLogin: boolean;

	private authenticated: boolean;

	private _lastVisitedUrl: string;
	private _showingAuthWarning: boolean;
	private publicationBeforeLogout: string;
	private loggedOut: boolean;

	private availablePublications: BbSelectOption[];
	private savedNextState: RouterStateSnapshot;

	private intervalSubscription: Subscription;
	private canDeactivateFromAngularJS = true;
	private haveSelectedPublication = false;

	private checkSessionRetries = 0;
	private checkSessionInterval = 15000;
	private ovpHomeUrl = '/home';
	private whitelabelSettingsApplied = false;
	private haveFetchedSecureCookieUrl = false;

	constructor(
		private sapiService: SapiService,
		private localStorageService: LocalStorageService,
		private router: Router,
		private locationService: LocationService,
		private overlayService: OverlayService,
		private translate: TranslateService,
		private modalRouteService: ModalRouteService,
		private publicationService: PublicationService
	) {
		this.userDataObs = new ReplaySubject(1);

		this.publicationsSubject = new ReplaySubject<BbSelectOption[]>(1);
		this.publicationBeforeLogout = null;
		this._showingAuthWarning = false;

		this.publicationService.getConfig().subscribe((config: Publication) => {
			if (config) {
				if (config.ovpCustomHomeUrl) {
					this.ovpHomeUrl = config.ovpCustomHomeUrl;
				}

				if (!this.whitelabelSettingsApplied && config) {
					this.whitelabelSettingsApplied = true;
					if (config.whitelabelFaviconId) {
						document.querySelectorAll('link[rel="icon"]').forEach(el => el.setAttribute('href', '/favicon.ico'));
					}
					if (config.whitelabelTitle) {
						document.title = config.whitelabelTitle;
					} else {
						document.title = 'Blue Billywig';
					}
					const env = this.locationService.getEnvironment() || '';
					if (env && env !== 'prod') {
						document.title += ` - ${env}`;
					}
				}
			}
		});
		this.isGlobalLogin = this.locationService.getGlobalLogin();
	}

	get lastVisitedUrl(): string {
		return this._lastVisitedUrl;
	}

	set lastVisitedUrl(url: string) {
		this._lastVisitedUrl = url;
	}

	isLoggedOut() {
		return this.loggedOut;
	}

	hasPublicationContext() {
		if (this.selectedPublication && this.selectedPublication.length > 0) {
			return true;
		} else if (this.userData && this.userData.currentContext && this.userData.currentContext.length > 0) {
			this.selectedPublication = this.userData.currentContext;
			return true;
		}
		return false;
	}

	isAuthenticated(): boolean {
		return this.authenticated;
	}

	setAuthenticated(authenticated: boolean) {
		this.authenticated = authenticated;
	}

	getSessionToken() {
		return this.localStorageService.get('ovp-sessionToken');
	}

	goToLocation() {
		if (this._lastVisitedUrl && this._lastVisitedUrl.length > 1) {
			const urlParts: string[] = this._lastVisitedUrl.split('?');
			if (urlParts.length > 1) {

				const params = urlParts[1].split('&');
				const queryParams: { [key: string]: any } = {};

				for (const param of params) {
					/* eslint-disable-next-line prefer-const */
					let [key, value] = param.split('=');
					value = decodeURIComponent(value);

					queryParams[key] = value;
				}
				// if a reacthash was set, redirect to the corresponding React page
				if (queryParams.hasOwnProperty('reacthash')) {
					const href = `${this.locationService.server}/react/#/?hash=${queryParams.reacthash}`;
					return window.location.href = href;
				} else {
					this.router.navigate([urlParts[0]], { queryParams });
				}
			} else {
				this.modalRouteService.navToInitialNavigationUrl(this._lastVisitedUrl);
			}
			this._lastVisitedUrl = null;
		} else {
			if (!this.router.url.startsWith('/login/reset')) {
				this.router.navigate([this.ovpHomeUrl]);
			}
		}
	}

	getUser(forceUpdate = false): Observable<User> {
		const userIsSameAsStored = this.userData && this.userData.id === this.localStorageService.get('ovp-currentUserId');

		if (!userIsSameAsStored) {
			this.userData = null;
		}

		if (forceUpdate) {
			// might have switched publications so all related calls can't use cache
			this.userDataRequestObs = null;
			this.userData = null;
		}

		if (!this.userDataRequestObs) {
			this.userDataRequestObs = this.sapiService.getUrl('/user/me')
				.pipe(
					share(),
					map((res) => {
						const user = (res as User);
						if (user.allowSetPasswordOnly === true) {
							if (!this.router.url.startsWith('/login/reset')) {
								this.logout(true).subscribe();
							}
							return null;
						} else {
							this.userData = user;
							if (this.userData && this.userData.id) {
								// console.warn('this.userData.id', this.userData.id)
								this.localStorageService.set('ovp-currentUserId', this.userData.id);
								this.selectedPublication = this.userData.currentContext;
							}
							return this.userData;
						}
					}),
					catchError((error: any) => {
						// this.userDataRequestObs = null;
						this.userData = null;
						this.localStorageService.remove('ovp-currentUserId');
						return of('error in authentication service getUser');
					})
				);
			this.userDataRequestObs.subscribe((data) => {
				if (data && data.id) {
					this.userDataObs.next(data);
				}
			});
		}

		return this.userDataObs;
	}

	switchToUser(userHandle: string): Observable<boolean> {
		this.userData = null;
		this.userDataRequestObs = null;

		const userSwitchedObservable = new Subject<boolean>();

		if (!this.switchUserRequestObs) {
			const params: any = {};
			if (userHandle.indexOf('@') > -1) {
				params.email = userHandle;
			} else {
				params.uid = userHandle;
			}
			this.switchUserRequestObs = this.sapiService.postUrl('/switchuser', params).pipe(
				map((res: Response) => {
					this.getUser(true);
					this.switchUserRequestObs = null;
					userSwitchedObservable.next(true);
					return res.ok;
				}),
				catchError((error: any) => {
					userSwitchedObservable.next(false);
					this.switchUserRequestObs = null;
					return observableThrowError(error);
				})
			);
			this.switchUserRequestObs.subscribe();
		}

		return userSwitchedObservable;
	}

	getAllowedCustomFields(entityName: string = 'mediaclip'): Observable<CustomField[]> {

		return this.publicationService.getCustomFields(entityName).pipe(
			map((allCustomFields: CustomField[]) => {
				const filteredFields: CustomField[] = [];

				allCustomFields.forEach(customField => {
					if (customField.visible || this.hasPermission('root')) {
						filteredFields.push(customField);
					}
				});

				return filteredFields;
			})
		);
	}

	hasPermission(permissionNames: string | string[]): Observable<boolean> {

		if (typeof permissionNames === 'string') {
			permissionNames = [permissionNames];
		}

		return combineLatest([this.getUser(), this.publicationService.getData()])
			.pipe(
				map((t: any) => {
					const user: User = t[0];
					const publication: Publication = t[1];

					if (user && (user.id === '0' || user.isPrivileged)) {
						return true; // root always allowed
					} else if (!user) {
						return false;
					}

					let contextPermissions: string[] = [];

					if (user.context && user.context.publications && publication &&
						user.context.publications[publication.id] && user.context.publications[publication.id].permissions) {

						contextPermissions = user.context.publications[publication.id].permissions;
						if (contextPermissions) {
							if (contextPermissions.indexOf('put') > -1 && contextPermissions.indexOf('delete') < 0) {
								contextPermissions.push('delete');
							}

							if (permissionNames && permissionNames.length > 0) {
								for (let i = 0; i < permissionNames.length; i++) {
									if (contextPermissions.indexOf(permissionNames[i]) < 0) {
										return false;
									}
								}
							}

							return true;
						}
					}

					return false;
				})
			);
	}

	checkSession(fromInterval = false): Observable<any> {
		// to test failures:
		// return this.sapiService.getUrl(Math.random() > 0.5 || !fromInterval ? '/auth' : '/aaa')
		return this.sapiService.getUrl('/auth')
			.pipe(
				map(() => {
					if (!this.intervalSubscription || this.checkSessionRetries) {
						if (this.intervalSubscription) {
							// if this.checkSessionRetries, reset to normal speed
							this.intervalSubscription.unsubscribe();
						}
						this.intervalSubscription = interval(this.checkSessionInterval).subscribe(() => {
							this.checkSession(true).subscribe();
						});
					}
					this.checkSessionRetries = 0;

					return of(true);
				}),
				share(),
				catchError((error: HttpErrorResponse) => {
					// If we get an undefined error something's gone wrong with the network, retry connecting in this case.
					if (error.status === 0) {
						return of(false);
					}
					this.checkSessionRetries++;
					if (this.intervalSubscription && this.checkSessionRetries >= 3) {
						this.intervalSubscription.unsubscribe();
						this.intervalSubscription = null;
					} else if (this.authenticated) {
						if (this.checkSessionRetries === 1) {
							if (this.intervalSubscription) {
								this.intervalSubscription.unsubscribe();
							}
							// allow three quicker retries before failing and showing the session expired message
							this.intervalSubscription = interval(this.checkSessionInterval / 3).subscribe(() => {
								this.checkSession(true).subscribe();
							});
						}
						return of(false);
					}

					this.publicationBeforeLogout = this.selectedPublication;
					this.reset();

					if (!this.router.url.startsWith('/login') && !this._showingAuthWarning) {
						if (this._lastVisitedUrl === '/') {
							this._lastVisitedUrl = null;
						}
						this._showingAuthWarning = true;
						if (fromInterval) {
							this._lastVisitedUrl = this.router.url;
							const notice: NoticeDefinition = {
								icon: 'warning',
								type: 'warning',
								title: this.translate.instant('SESSION_EXPIRED_TITLE'),
								contents: this.translate.instant('SESSION_EXPIRED'),
								onConfirm: () => {
									this._showingAuthWarning = false;
									this.router.navigate(['/login']);
								}
							};
							this.overlayService.createNotice(notice);
						} else {
							this.router.navigate(['/login']);
						}
					}

					return observableThrowError(error);
				})
			);
	}

	login(username: string, password: string): Observable<any> {
		if (typeof password === 'undefined') {
			password = '';
		}

		this.loggedOut = false;

		return this.sapiService.getRandom()
			.pipe(
				mergeMap((result: any) => {
					const random = result.body;

					const passwordBase64Md5 = btoa(md5.start().appendStr(password).end().toString());
					const base64Token = btoa(md5.start().appendStr(passwordBase64Md5).appendAsciiStr(random).end().toString());

					return this.loginWithHash(username, base64Token);
				}),
				catchError((error: any) => observableThrowError(error))
			);
	}

	loginWithHash(username: string, password: string): Observable<any> {

		const credentials = {
			username,
			password,
			sesstoken: 'set'
		};

		return this.sapiService.postUrl('/auth/login', credentials)
			.pipe(
				map((response: any) => {
					this.setSessionToken(response);
					this.localStorageService.set('ovp-lastVisit', new Date().getTime());
					this.authenticated = true;
					this.checkSession().subscribe();
					this.updateHubspotContact();
					return response;
				}),
				catchError((error: any) => observableThrowError(error))
			);
	}

	changePasswordByHash(oldPasswordHash: string, newPassword: string): Observable<any> {
		return this.sapiService.postUrl('/user/me/changePassword', {
			oldpassword: oldPasswordHash,
			newpassword: btoa(md5.start().appendStr(newPassword).end().toString())
		});
	}

	getAvailablePublications() {
		return this.availablePublications;
	}

	checkForPublication(force: boolean = true) {
		this.haveSelectedPublication = false;

		this.getUser(force).subscribe((userInfo: User) => {
			// this.busy = false;
			if (!userInfo) {
				return;
			}

			if (this.publicationBeforeLogout || userInfo.currentContext) {
				if (force && !this.haveSelectedPublication) {
					this.selectPublication(this.publicationBeforeLogout || userInfo.currentContext);
					this.haveSelectedPublication = true;
				}
				this.publicationBeforeLogout = null;
				return;
			}

			const preferredPublication = this.localStorageService.get('ovp-preferredPublication');

			const availablePublications: BbSelectOption[] = Array<BbSelectOption>();

			let publicationCount = -1;
			if (userInfo && userInfo.publications) {
				publicationCount = userInfo.publications.length;
			}

			if (publicationCount === 1 && !this.haveSelectedPublication && userInfo.publications[0]) {
				this.selectPublication(userInfo.publications[0].name);
				this.haveSelectedPublication = true;
				return;
			}

			for (let i = 0; i < publicationCount; i++) {
				const publication = userInfo.publications[i];

				if (preferredPublication === publication.name) {
					if (!this.haveSelectedPublication) {
						this.selectPublication(publication.name);
						this.haveSelectedPublication = true;
					}
					return;
				}

				availablePublications.push({
					value: publication.name,
					label: (publication.label || publication.name)
				});
			}

			this.availablePublications = availablePublications;
			this.publicationsSubject.next(availablePublications);

			this.router.navigate(['/login/show-publications']);
		});
	}

	selectPublication(publication: string, rememberPublicationSelection?: boolean) {
		this.selectedPublication = publication;

		if (rememberPublicationSelection) {
			this.localStorageService.set('ovp-preferredPublication', this.selectedPublication);
		}

		if (this.isGlobalLogin) {
			this.switchToPublication(this.selectedPublication)
				.pipe(
					catchError((error: any) => {
						this.selectedPublication = null;
						return observableThrowError(error);
					}),
					first()
				)
				.subscribe(() => {
					this.getSecureCookieUrl();
					this.goToLocation();
				});
		} else {
			this.publicationService.getData(true);
			this.getSecureCookieUrl();
			this.goToLocation();
		}
	}

	getSecureCookieUrl() {
		// set cookie if there is a secureMediaAssetPath
		this.publicationService.getData().subscribe((publication: Publication) => {
			if (publication && publication.secureMediaAssetPath !== '' && !this.haveFetchedSecureCookieUrl) {
				this.haveFetchedSecureCookieUrl = true;
				this.getSecureMediaAssetPathCookieUrl().subscribe((secureMediaAssetPathCookieUrl: any) => {
					// placing the URL in an iFrame to set cookie
					if (secureMediaAssetPathCookieUrl && secureMediaAssetPathCookieUrl.body) {
						const element: HTMLIFrameElement = document.createElement('iframe');
						element.className = 'secureMediaAssetPath-iframe';
						element.src = secureMediaAssetPathCookieUrl.body;
						element.style.width = '0';
						element.style.height = '0';
						document.querySelector('body').appendChild(element);
					}
				});
			}
		});
	}

	reset(loggingOut: boolean = false) {
		this.localStorageService.remove('ovp-sessionToken');
		this.localStorageService.remove('ovp-currentUserId');
		this.authenticated = false;
		this.loggedOut = true;
		this.availablePublications = null;
		this.selectedPublication = null;
		this.publicationService.reset();
		this.publicationsSubject.next(null);
		if (!loggingOut) {
			this.userDataObs.next(null);
		}
	}

	logout(navigateToLogin: boolean = true): Observable<void> {
		setTimeout(() => {
			// avoid expresison changed angular error
			this.reset(true);
		});

		return this.sapiService.getUrl('/auth/logout').pipe(
			tap(() => {
				this.userDataObs.next(null);
				if (navigateToLogin) {
					this.router.navigate(['/login']);
				};
			}),
			share()
		);
	};

	switchToPublication(publicationName: string, otherPublication: boolean = false) {
		return this.sapiService.getUrl('/switchcontext?publication=' + publicationName).pipe(
			map((response: any) => {
				this.setSessionToken(response);
				this.publicationService.getData(true);
				if (otherPublication) {
					this.getUser(true);
					// this.router.navigate([this.router.url]);
					window.location.reload();
				}
				return response;
			})
		);
	}

	getSecureMediaAssetPathCookieUrl() {
		return this.sapiService.getUrl('/publication/getcfscreq');
	}

	requestNewPassword(email: string) {
		return this.sapiService.postUrl('/user/me/resetPassword', { email });
	}

	// temporary candeactivate management between AngularJS and Angular 2+
	// remove when all angularjs detailspages are totally migrated
	setCanDeactivateFromAngularJS(canDeactivate: boolean) {
		this.canDeactivateFromAngularJS = canDeactivate;
	}

	getCanDeactivateFromAngularJS(): boolean {
		return this.canDeactivateFromAngularJS;
	}

	angularWantsToNavigate() {
		this.angularWantsToNavigateSubject.next(true);
		setTimeout(() => {
			this.angularWantsToNavigateSubject.next(false);
		});
	}

	saveNextState(nextState: RouterStateSnapshot) {
		this.savedNextState = nextState;
	}

	goToSavedURL() {
		if (this.savedNextState) {
			this.router.navigate([this.savedNextState.url]);
		}
	}
	// END of temporary candeactivate management between AngularJS and Angular 2+

	private updateHubspotContact(): void {
		const browser = Bowser.parse(navigator.userAgent);
		const params = {
			browser: browser.browser.name,
			browser_version: browser.browser.version,
			operating_system: `${browser.os.name} ${browser.os.version} (${browser.platform.type})`,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
		};
		this.sapiService.postUrl('/user/me/updateHubspotContact', params).subscribe();
	}

	private setSessionToken(response: SapiEntity) {
		if (response && response.id) {
			const sessionToken = response.id;
			if (sessionToken !== 'set') {
				this.localStorageService.set('ovp-sessionToken', sessionToken);
			}
		}
	}
}
